// deps
import React, { memo, useMemo } from "react";

// internal-deps
import { navigateToDeeplink } from "@swiggy-private/checkout-core";
import { format, getCloudinaryUrl, isEmpty, TEAM_LINE } from "@swiggy-private/js-utils";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// styles
import {
    BgContainer,
    BgContainerWrapper,
    DividingLine,
    GreetingText,
    StatusDescription,
    StatusText,
    StatusTextWrapper,
    StyledImage,
    StyledImageSelfContained,
    SubscriptionMetaWrapper,
} from "./TableBookingStatusBanner.styles";

// constants
import {
    BANNER_HEIGHT,
    SacramentoFont,
    TABLE_BOOKING_STATUS_DETAILS as STATUS_DETAILS,
} from "@dineout-payment-success-ui/constants";

// helpers
import { getBookingStatusHelperText, getColorCode } from "@dineout-payment-success-ui/helpers/BookingSuccessHelpers";

// interfaces
import { ITableBookingStatusBannerProps } from "@dineout-payment-success-ui/interfaces";
import { PAYMENT_SUCCESS_BANNER } from "@swiggy-private/payment-test-ids";

const SelfContainedTableBanner = (props: ITableBookingStatusBannerProps) => {
    const { bannerImage } = props;
    return (
        <StyledImageSelfContained>
            <StyledImage src={bannerImage.src} />
        </StyledImageSelfContained>
    );
};

const CustomizableTableBanner = (props: ITableBookingStatusBannerProps) => {
    const { bannerImage, titleText, titleTextColor, subscriptionMeta, bookingStatusText, cta, girfLogo } = props;

    return (
        <BgContainerWrapper
            style={{
                height:
                    isEmpty(subscriptionMeta?.title) && isEmpty(subscriptionMeta?.subtitle)
                        ? BANNER_HEIGHT.DEFAULT
                        : BANNER_HEIGHT.WITH_SUBSCRIPTION_META,
            }}
            data-testid={PAYMENT_SUCCESS_BANNER.RESERVATION_STATUS}
        >
            <BgContainer src={bannerImage.src}></BgContainer>
            {girfLogo ? (
                <Image src={girfLogo ? getCloudinaryUrl(girfLogo, {}, { teamLine: TEAM_LINE.DINEOUT }) : ""} />
            ) : null}
            <StatusTextWrapper data-testid={PAYMENT_SUCCESS_BANNER.BOOKING_TEXT_STATUS}>
                <SacramentoFont />
                {titleText && (
                    <StatusText textColor={titleTextColor} colorKey={getColorCode(bookingStatusText)}>
                        {titleText}
                    </StatusText>
                )}
                {subscriptionMeta && (
                    <SubscriptionMetaWrapper>
                        {subscriptionMeta.title && subscriptionMeta.subtitle && <DividingLine />}
                        {subscriptionMeta.title && (
                            <GreetingText variant="V2.heading2.neutral.bold">{subscriptionMeta.title}</GreetingText>
                        )}
                        {subscriptionMeta.subtitle && (
                            <StatusDescription
                                variant="V2.body3.neutral.regular"
                                color="positive"
                                colorKey={getColorCode(bookingStatusText)}
                                dangerouslySetInnerHTML={{ __html: subscriptionMeta.subtitle }}
                                onClick={() => {
                                    cta?.link && navigateToDeeplink(cta.link);
                                }}
                            ></StatusDescription>
                        )}
                    </SubscriptionMetaWrapper>
                )}
            </StatusTextWrapper>
        </BgContainerWrapper>
    );
};

const TableBookingStatusBanner = (props: ITableBookingStatusBannerProps) => {
    const { bannerImage, titleText, titleTextColor, bookingStatusText, cta, subscriptionMeta, girfLogo } = props;

    const bgImageUrl = useMemo(
        () =>
            getCloudinaryUrl(
                bannerImage.src || STATUS_DETAILS?.[bookingStatusText].BACKGROUND || "",
                {},
                { teamLine: TEAM_LINE.DINEOUT },
            ),
        [bannerImage?.src, bookingStatusText],
    );

    const bookingStatusHelperText = getBookingStatusHelperText(false, bookingStatusText);

    /* This is a fallback handling in case PL api fails to return the presentational data */
    const updatedTitleText = useMemo(() => {
        if (
            isEmpty(bannerImage?.src) &&
            isEmpty(titleText) &&
            isEmpty(subscriptionMeta?.title) &&
            isEmpty(subscriptionMeta?.subtitle)
        ) {
            return format(STATUS_DETAILS?.[bookingStatusText]?.TEXT, bookingStatusHelperText);
        }
        return titleText;
    }, [bannerImage?.src, bookingStatusHelperText, bookingStatusText, subscriptionMeta, titleText]);

    return bannerImage.isBackgroundImageWithContent ? (
        <SelfContainedTableBanner bannerImage={{ src: bgImageUrl || "" }} bookingStatusText={bookingStatusText} />
    ) : (
        <CustomizableTableBanner
            bannerImage={{ src: bgImageUrl || "" }}
            titleText={updatedTitleText}
            titleTextColor={titleTextColor}
            bookingStatusText={bookingStatusText}
            cta={cta}
            subscriptionMeta={subscriptionMeta}
            girfLogo={girfLogo}
        />
    );
};

export default memo(TableBookingStatusBanner);
